import React, { createContext, useContext } from 'react';
import { Auth0ProviderWithNavigate } from './Auth0ProviderWithNavigate';

interface AuthProviderProps {
  children: React.ReactNode;
}

// Create context to track if Auth0 is properly configured
const AuthConfigContext = createContext<{ isConfigured: boolean }>({ isConfigured: false });

export const useAuthConfig = () => useContext(AuthConfigContext);

/**
 * Auth0 Provider configuration for the FogPred application
 * Implements Auth0 best practices with PKCE flow and in-memory token storage
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Check if authentication is enabled via feature flag
  const isAuthEnabled = import.meta.env.VITE_AUTH_ENABLED === 'true';
  
  // Get Auth0 configuration from environment variables
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const redirectUri = import.meta.env.VITE_AUTH0_REDIRECT_URI || window.location.origin;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

  // Check if Auth0 is properly configured
  const isConfigured = isAuthEnabled && domain && clientId;

  // If auth is not enabled or not configured, render children with context
  if (!isAuthEnabled || !isConfigured) {
    if (isAuthEnabled && !isConfigured) {
      console.error('Auth0 configuration missing: VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID are required');
    }
    return (
      <AuthConfigContext.Provider value={{ isConfigured: false }}>
        {children}
      </AuthConfigContext.Provider>
    );
  }

  return (
    <AuthConfigContext.Provider value={{ isConfigured: true }}>
      <Auth0ProviderWithNavigate
        domain={domain}
        clientId={clientId}
        redirectUri={redirectUri}
        audience={audience}
      >
        {children}
      </Auth0ProviderWithNavigate>
    </AuthConfigContext.Provider>
  );
};